import { useErrorMessages, useUserSession } from '@AppModels/hooks/appStateHooks';
import { IModalContext, ModalChildComponentContextKey } from '@AppModels/index';
import { useEventLogging } from '@AppModels/Logging';
import { numberToFormattedCount } from '@AppModels/utils/numberUtils';
import { parseSkippedLockedTags } from '@AppModels/utils/tagUtils';
import { useContactRemoveTagsMutation } from '@Queries/Contact/useContactRemoveTagsMutation';
import { useGetSystemJobQuery } from '@Queries/SystemJob/useGetSystemJobQuery';
import { getTagSearchQueryOptions } from '@Queries/Tag/useTagSearchQuery';
import * as Api from '@ViewModels';
import { asModalComponent } from '@WebComponents/Modal';
import { useQueries } from '@tanstack/react-query';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components-v2/Button';
import { Checkbox } from '../../../components-v2/Checkbox';
import { Pill } from '../../../components-v2/Pill/Pill';
import { DeprecatedCloseButton } from '../../DeprecatedCloseButton';
import { LoadingSpinner } from '../../LoadingSpinner';
import { LockIcon } from '../../svgs/icons/LockIcon';
import { Tag } from '../../tags/Tag';
import { styleSheet } from './styles';

interface Props extends IModalContext<string> {
	tagNames: string[];
	request?: Api.IBulkContactsRequest;
	totalContactCount?: number;
}

export function _ContactsRemoveTag({ request, tagNames, totalContactCount, parentModal }: Props) {
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();
	const { logInput, logApiError } = useEventLogging('ContactsRemoveTag');
	const isAdmin = !!userSession.account.isAdmin;

	// Look up each tag to surface lock state. The API will reject locked-tag removals
	// for non-admins, but we want to gate the UI so they don't get a 403 they can't act
	// on. Reuse getTagSearchQueryOptions so the queryKey + fetcher stay in sync with
	// useTagSearchQuery — refreshTags / invalidateAllTagSearchQueries cover these too.
	const tagInfoQueries = useQueries({
		queries: tagNames.map(name => ({
			...getTagSearchQueryOptions(userSession, { query: name }),
			staleTime: 60_000,
		})),
	});

	// useQueries returns a new array reference each render (TQ v5), so we hash the
	// underlying lock data into a stable key and recompute downstream values only when
	// it changes. Lookup is done at the consumer (rather than via `select`) so the
	// cached payload matches useTagSearchQuery exactly.
	const lockStateKey = tagInfoQueries
		.map((q, i) => {
			const name = tagNames[i];
			const matched = q.data?.find(t => t.name?.toLocaleLowerCase() === name?.toLocaleLowerCase());
			return `${name}:${matched?.isLocked ? 1 : 0}`;
		})
		.join('|');

	const tagsByName = React.useMemo(() => {
		const map = new Map<string, Api.IAccountTag>();
		tagInfoQueries.forEach((q, i) => {
			const name = tagNames[i];
			if (!name) return;
			const matched = q.data?.find(t => t.name?.toLocaleLowerCase() === name.toLocaleLowerCase());
			if (matched) map.set(name, matched);
		});
		return map;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lockStateKey]);

	const lockedNames = React.useMemo(() => {
		const names = new Set<string>();
		tagsByName.forEach((tag, name) => {
			if (tag.isLocked) names.add(name);
		});
		return names;
	}, [tagsByName]);

	const lockedNamesArr = React.useMemo(() => Array.from(lockedNames), [lockedNames]);

	// Falls back to a name-only IAccountTag when the lookup hasn't resolved yet.
	const resolveTag = (name: string): Api.IAccountTag => tagsByName.get(name) ?? { name };

	// Treat both pending and background-refetch as "loading" so we don't enable Confirm
	// while stale lock data is being refreshed (TQ v5: isLoading is false during refetch).
	const lockStateLoading = !isAdmin && tagInfoQueries.some(q => q.isPending || q.isFetching);
	const allLockedForNonAdmin = !isAdmin && lockedNames.size > 0 && lockedNames.size === tagNames.length;
	const hasLockedTagsForNonAdmin = !isAdmin && lockedNames.size > 0;

	// State to track which tags are selected for removal. For non-admins, locked names
	// drop out of the default selection so they can't accidentally fire a 403.
	const [selectedTags, setSelectedTags] = React.useState<string[]>(tagNames);

	// Update selected tags when tagNames prop changes
	React.useEffect(() => {
		setSelectedTags(tagNames);
	}, [tagNames]);

	// When lock info arrives, drop locked names from the selection for non-admins so
	// the Confirm action only sends what's actually removable.
	React.useEffect(() => {
		if (isAdmin || lockedNames.size === 0) return;
		setSelectedTags(prev => prev.filter(t => !lockedNames.has(t)));
	}, [isAdmin, lockedNames]);

	const contactRemoveTagsMutation = useContactRemoveTagsMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('ContactRemoveTagsMutation', error);
		},
	});

	const systemJobQuery = useGetSystemJobQuery({
		enabled: contactRemoveTagsMutation.isSuccess,
		jobId: contactRemoveTagsMutation.data?.id,
		refetchInterval: query => {
			const data = query.state.data;
			if (!data) {
				return false;
			}
			if (data.percentComplete < 100) {
				return 2000;
			}
			return false;
		},
	});

	const isSystemJobComplete =
		systemJobQuery.data?.percentComplete != null && systemJobQuery.data?.percentComplete >= 100;

	React.useEffect(() => {
		if (isSystemJobComplete) {
			const skipped = parseSkippedLockedTags(systemJobQuery.data?.additionalFields);
			if (skipped.length > 0) {
				toast.warning(`Skipped locked tags: ${skipped.join(', ')}.`);
			}
			parentModal?.onRequestClose(selectedTags.join(','), false);
		}
	}, [isSystemJobComplete, selectedTags, parentModal, systemJobQuery.data]);

	const onCloseClicked = () => {
		if (parentModal) {
			parentModal.onRequestClose(null, true);
		}
	};

	const toggleTagSelection = (tagName: string) => {
		if (!isAdmin && lockedNames.has(tagName)) return;
		setSelectedTags(prev => (prev.includes(tagName) ? prev.filter(t => t !== tagName) : [...prev, tagName]));
	};

	const onConfirmRemovalClicked = () => {
		if (selectedTags.length === 0 || contactRemoveTagsMutation.isPending) {
			return;
		}

		logInput('Remove', 'Click', { tags: selectedTags });
		contactRemoveTagsMutation.mutate({
			request: {
				...request,
				tags: selectedTags,
			},
		});
	};

	const contactCountLabel = React.useMemo(() => {
		const count = totalContactCount ?? request?.includeContactIds?.length ?? 0;
		if (count === 0) {
			return 'selected contacts';
		}
		return `${numberToFormattedCount(count)} ${count === 1 ? 'contact' : 'contacts'}`;
	}, [request?.includeContactIds?.length, totalContactCount]);

	return (
		<div className={`${css(styleSheet.container)} contacts-remove-tag`}>
			<div className={css(styleSheet.header)}>
				<DeprecatedCloseButton
					disabled={
						contactRemoveTagsMutation.isPending || (contactRemoveTagsMutation.isSuccess && !isSystemJobComplete)
					}
					onClick={onCloseClicked}
				/>
			</div>
			<div className={css(styleSheet.title)}>
				{allLockedForNonAdmin ? (tagNames.length === 1 ? 'Tag Locked' : 'Tags Locked') : 'Remove Tag'}
			</div>
			{contactRemoveTagsMutation.isSuccess && !isSystemJobComplete ? (
				<div className={css(styleSheet.removingMessage)}>
					<div>Removing tag...</div>
					<LoadingSpinner type='small' />
				</div>
			) : allLockedForNonAdmin ? (
				<div className={css(styleSheet.content)}>
					<div className={css(styleSheet.lockedInfoMessage)}>
						{tagNames.length === 1 ? (
							<>
								The
								<Tag styles={[styleSheet.lockedInfoTag]} tagValue={resolveTag(tagNames[0])} />
								tag is locked. Only admins can remove this tag from contacts.
							</>
						) : (
							<>
								The
								{lockedNamesArr.map(name => (
									<Tag key={name} styles={[styleSheet.lockedInfoTag]} tagValue={resolveTag(name)} />
								))}
								tags are locked. Only admins can remove them from contacts.
							</>
						)}
					</div>
					<div className={css(styleSheet.buttonContainer)}>
						<Button variant='primary' styles={[styleSheet.confirmButton]} onClick={onCloseClicked}>
							Close
						</Button>
					</div>
				</div>
			) : (
				<div className={css(styleSheet.content)}>
					<div className={css(styleSheet.message)}>
						{tagNames.length === 1 ? (
							<>
								Remove <Pill variant='tag' label={tagNames[0]} color='blue' />
								{lockedNames.has(tagNames[0]) && (
									<span
										className={css(styleSheet.lockBadge)}
										title="This tag is locked. Only admins can add or remove it from contacts. Locking doesn't affect tags applied through integrations, automations, or surveys."
									>
										<LockIcon />
									</span>
								)}{' '}
								from {contactCountLabel}?
							</>
						) : (
							<>Select tags to remove from {contactCountLabel}:</>
						)}
					</div>
					{hasLockedTagsForNonAdmin && (
						<div className={css(styleSheet.lockedWarning)}>
							<LockIcon />
							<span>
								Only admins can remove the locked {lockedNames.size === 1 ? 'tag' : 'tags'}: {lockedNamesArr.join(', ')}
								. The remaining {selectedTags.length === 1 ? 'tag' : 'tags'} will still be removed.
							</span>
						</div>
					)}
					{tagNames.length > 1 && (
						<div className={css(styleSheet.tagSelectionContainer)}>
							{tagNames.map(tagName => {
								const locked = lockedNames.has(tagName);
								const lockedForUser = locked && !isAdmin;
								return (
									<label
										key={tagName}
										className={css(styleSheet.tagCheckboxLabel, lockedForUser && styleSheet.tagCheckboxLabelLocked)}
									>
										<Checkbox
											checked={selectedTags.includes(tagName)}
											onCheckedChange={() => toggleTagSelection(tagName)}
											disabled={contactRemoveTagsMutation.isPending || lockedForUser}
										/>
										<Pill variant='tag' label={tagName} color='blue' />
										{locked && (
											<span
												className={css(styleSheet.lockBadge)}
												title="This tag is locked. Only admins can add or remove it from contacts. Locking doesn't affect tags applied through integrations, automations, or surveys."
											>
												<LockIcon />
											</span>
										)}
									</label>
								);
							})}
						</div>
					)}
					<div className={css(styleSheet.subMessage)}>
						This action cannot be undone. The contacts will remain but the {selectedTags.length === 1 ? 'tag' : 'tags'}{' '}
						will be removed.
					</div>
					<div className={css(styleSheet.buttonContainer)}>
						<Button
							variant='secondary'
							styles={[styleSheet.cancelButton]}
							onClick={onCloseClicked}
							disabled={contactRemoveTagsMutation.isPending}
						>
							Cancel
						</Button>
						<Button
							variant='destructive'
							styles={[styleSheet.confirmButton]}
							onClick={onConfirmRemovalClicked}
							disabled={selectedTags.length === 0 || lockStateLoading}
							isLoading={contactRemoveTagsMutation.isPending}
							title={lockStateLoading ? 'Checking lock status…' : undefined}
						>
							Confirm Removal
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}

const ContactsRemoveTagAsObserver = observer(_ContactsRemoveTag);
export const ContactsRemoveTag = inject(ModalChildComponentContextKey)(ContactsRemoveTagAsObserver);

export const ContactsRemoveTagModal = asModalComponent(ContactsRemoveTag, {
	className: 'contacts-remove-tag-modal',
	shouldCloseOnOverlayClick: false,
});
